import store from '@/store';
import { trackSmartSeatsSelection } from 'metrics/user-analytics/seats';
import quickPurchaseInitialization, {
  createQuickPurchaseAttemptLoadingOwnerId,
} from './quickPurchaseInitialization';
import { normalizeSeatNumber, parseSeatFloor } from './seatPreferenceMetadata';
import { PASSENGER_COUNTS, POSITION_ORDER } from './seatPreferenceConstants';
import { INDIVIDUAL_MATCH } from './seatPreferenceSelection';

const MATCHES = new Set(Object.values(INDIVIDUAL_MATCH));

const currentPurchase = () => store.getState().purchase.toJS();
const owns = (object, key) => Object.prototype.hasOwnProperty.call(object || {}, key);

const cloneValue = (value, seen = new WeakMap()) => {
  if (!value || typeof value !== 'object') return value;
  if (seen.has(value)) return seen.get(value);

  const clone = Array.isArray(value) ? [] : {};
  seen.set(value, clone);
  Object.keys(value).forEach((key) => {
    clone[key] = cloneValue(value[key], seen);
  });
  return clone;
};

const freezeValue = (value, seen = new WeakSet()) => {
  if (!value || typeof value !== 'object' || seen.has(value)) return value;
  seen.add(value);
  Object.keys(value).forEach((key) => freezeValue(value[key], seen));
  return Object.freeze(value);
};

const immutableClone = (value) => freezeValue(cloneValue(value));

const normalizeIdentity = (value) => {
  if (value === undefined || value === null || value === '') {
    return { status: 'missing', value: null };
  }
  if (typeof value === 'number') {
    return Number.isFinite(value)
      ? { status: 'valid', value: String(value) }
      : { status: 'invalid', value: null };
  }
  if (typeof value !== 'string' || !value.trim()) {
    return { status: 'invalid', value: null };
  }
  return { status: 'valid', value: value.trim() };
};

const normalizeNumberIdentity = (value) => {
  const number = normalizeSeatNumber(value);
  return number === null
    ? {
        status: value === undefined || value === null || value === '' ? 'missing' : 'invalid',
        value: null,
      }
    : { status: 'valid', value: number };
};

const readAliases = (object, keys, normalize) => {
  const presentKeys = keys.filter((key) => owns(object, key));
  if (!presentKeys.length) return { status: 'missing', value: null };

  const values = presentKeys.map((key) => normalize(object[key]));
  if (values.some(({ status }) => status === 'invalid')) {
    return { status: 'invalid', value: null };
  }
  const valid = values.filter(({ status }) => status === 'valid');
  if (!valid.length) return { status: 'missing', value: null };
  if (valid.length !== values.length || valid.some(({ value }) => value !== valid[0].value)) {
    return { status: 'invalid', value: null };
  }
  return valid[0];
};

const floorAliases = (object, keys) => readAliases(object, keys, parseSeatFloor);
const identityAliases = (object, keys) => readAliases(object, keys, normalizeIdentity);
const numberAliases = (object, keys) => readAliases(object, keys, normalizeNumberIdentity);

const normalizeTicketState = (value) => {
  const normalized = normalizeIdentity(value);
  return normalized.status === 'valid'
    ? { status: 'valid', value: normalized.value.toLowerCase() }
    : normalized;
};

const normalizePosition = (position) => {
  if (position === null || position === undefined) return null;
  if (!Array.isArray(position) || !position.length) return undefined;
  const unique = new Set(position);
  if (unique.size !== position.length || position.some((item) => !POSITION_ORDER.includes(item))) {
    return undefined;
  }
  return POSITION_ORDER.filter((item) => unique.has(item));
};

const primarySeats = (seats) =>
  Array.isArray(seats) ? seats.filter((seat) => seat && !seat.isPickedAsAdjacent) : [];

const seatFloor = (seat) => {
  const parsed = parseSeatFloor(seat?.seatLevel);
  return parsed.status === 'valid' ? parsed.value : null;
};

const selectionFingerprint = (result) =>
  JSON.stringify([
    result.tripSlug,
    result.passengerCount,
    result.algorithmFallback,
    result.matches.map((match, index) => [
      index,
      normalizeSeatNumber(result.seats[index]?.number),
      seatFloor(result.seats[index]),
      match.match,
      match.preference,
      match.identityEvidence,
    ]),
  ]);

/**
 * Normalizes and freezes one supported smart result without strengthening captured identity.
 * @param {object} result - Ordered one- or two-passenger selection result.
 * @returns {object|null} Immutable validated result, or null for an incomplete invariant.
 */
const normalizeSmartResult = (result) => {
  if (
    !result ||
    !PASSENGER_COUNTS.includes(result.passengerCount) ||
    !result.tripSlug ||
    !Array.isArray(result.seats) ||
    result.seats.length !== result.passengerCount ||
    !Array.isArray(result.matches) ||
    result.matches.length !== result.passengerCount
  ) {
    return null;
  }

  const usedSeatIds = new Set();
  const normalizedMatches = result.matches.map((match, passengerIndex) => {
    const preferenceNumber = normalizeSeatNumber(match?.preference?.number);
    const actualNumber = normalizeSeatNumber(result.seats[passengerIndex]?.number);
    const position = normalizePosition(match?.preference?.position);
    const selectedFloor = parseSeatFloor(match?.identityEvidence?.selectedFloor);
    const originalRequestFloor = parseSeatFloor(match?.identityEvidence?.originalRequestFloor);
    const preferenceFloor = parseSeatFloor(match?.preference?.floor);
    const seatId = Array.isArray(match?.seatId) ? JSON.stringify(match.seatId) : null;
    const numberIdentity = match?.identityEvidence?.numberIdentity;

    if (
      match?.passengerIndex !== passengerIndex ||
      !MATCHES.has(match?.match) ||
      preferenceNumber === null ||
      actualNumber !== preferenceNumber ||
      position === undefined ||
      !seatId ||
      usedSeatIds.has(seatId) ||
      !['unique', 'ambiguous', 'unknown'].includes(numberIdentity) ||
      selectedFloor.status === 'invalid' ||
      originalRequestFloor.status === 'invalid' ||
      preferenceFloor.status === 'invalid' ||
      (preferenceFloor.status === 'valid' &&
        selectedFloor.status === 'valid' &&
        preferenceFloor.value !== selectedFloor.value) ||
      normalizeSeatNumber(match?.identityEvidence?.number) !== actualNumber
    ) {
      return null;
    }
    usedSeatIds.add(seatId);

    return {
      passengerIndex,
      seatId: cloneValue(match.seatId),
      preference: {
        number: actualNumber,
        floor: preferenceFloor.status === 'valid' ? preferenceFloor.value : null,
        position,
      },
      match: match.match,
      identityEvidence: {
        number: actualNumber,
        numberIdentity,
        selectedFloor: selectedFloor.status === 'valid' ? selectedFloor.value : null,
        originalRequestFloor:
          originalRequestFloor.status === 'valid' ? originalRequestFloor.value : null,
        requestCanDisambiguateFloor: match.identityEvidence.requestCanDisambiguateFloor === true,
      },
    };
  });

  if (normalizedMatches.some((match) => match === null)) return null;

  const normalized = {
    tripSlug: String(result.tripSlug),
    passengerCount: result.passengerCount,
    seats: result.seats.map((seat) => cloneValue(seat)),
    matches: normalizedMatches,
    algorithmFallback: result.algorithmFallback === true,
  };
  return immutableClone(normalized);
};

const selectedTrip = (trips) => {
  if (
    !trips ||
    !Array.isArray(trips.departs) ||
    trips.departs.length !== 1 ||
    trips.returns !== undefined
  ) {
    return null;
  }
  const [trip] = trips.departs;
  return trip?.id ? trip : null;
};

const tripMatchesResult = (trips, result) => {
  const trip = selectedTrip(trips);
  if (!trip || String(trip.id) !== result.tripSlug) return false;
  const seats = primarySeats(trip.selectedSeats);
  if (seats.length !== result.passengerCount) return false;
  return seats.every((seat, index) => {
    const number = normalizeSeatNumber(seat.number);
    const expectedNumber = normalizeSeatNumber(result.seats[index]?.number);
    const expectedFloor = result.matches[index].identityEvidence.originalRequestFloor;
    return number === expectedNumber && seatFloor(seat) === expectedFloor;
  });
};

const currentTripMatches = (purchase, result) => {
  const tripSlug = purchase?.departs?.tripSlug || purchase?.departs?.fragments?.[0]?.id;
  return !tripSlug || String(tripSlug) === result.tripSlug;
};

const uniquePassengerIds = (passengers, count) => {
  if (!Array.isArray(passengers) || passengers.length !== count) return null;
  const ids = passengers.map(({ id }) => normalizeIdentity(id));
  if (ids.some(({ status }) => status !== 'valid')) return null;
  const values = ids.map(({ value }) => value);
  return new Set(values).size === values.length ? values : null;
};

const expectedTicketsFor = (result, passengerIds, mode) =>
  result.matches.map((match, passengerIndex) => ({
    passengerIndex,
    passengerId: passengerIds[passengerIndex],
    tripSlug: result.tripSlug,
    number: match.preference.number,
    selectedFloor: match.identityEvidence.selectedFloor,
    originalRequestFloor: match.identityEvidence.originalRequestFloor,
    requestCanDisambiguateFloor:
      mode === 'modern' && match.identityEvidence.requestCanDisambiguateFloor,
    numberIdentity: match.identityEvidence.numberIdentity,
  }));

const normalizeSegment = (segment) => {
  const way = segment?.way === 'departure' ? 'departs' : segment?.way;
  const requestTrip = normalizeIdentity(segment?.requestTripSlug);
  const receiptTrip = identityAliases(segment?.receiptSegment, ['trip_slug', 'tripSlug']);
  const envelopeToken = identityAliases(segment?.receiptEnvelope, [
    'purchase_token',
    'purchaseToken',
    'token',
  ]);
  const containerToken = identityAliases(segment?.receiptContainer, [
    'purchase_token',
    'purchaseToken',
    'token',
  ]);
  return {
    valid:
      way === 'departs' &&
      requestTrip.status === 'valid' &&
      receiptTrip.status !== 'invalid' &&
      envelopeToken.status !== 'invalid' &&
      containerToken.status !== 'invalid',
    way,
    requestTripSlug: requestTrip.value,
    receiptTripSlug: receiptTrip.value,
    receiptTripStatus: receiptTrip.status,
    envelopeToken: envelopeToken.value,
    envelopeTokenStatus: envelopeToken.status,
    containerToken: containerToken.value,
    containerTokenStatus: containerToken.status,
    status: segment?.status,
    requestTickets: Array.isArray(segment?.requestTickets) ? segment.requestTickets : null,
    tickets: Array.isArray(segment?.tickets) ? segment.tickets : null,
  };
};

const normalizeTicket = (ticket) => {
  const number = numberAliases(ticket, ['seat', 'number']);
  const passenger = identityAliases(ticket, ['passenger_id', 'passengerId']);
  const trip = identityAliases(ticket, ['trip_slug', 'tripSlug']);
  const floor = floorAliases(ticket, ['seat_level', 'seatLevel']);
  const seatFloorValue = floorAliases(ticket, ['seat_floor', 'seatFloor']);
  const id = identityAliases(ticket, ['id', 'ticket_id', 'ticketId']);
  const state = readAliases(ticket, ['state', 'status'], normalizeTicketState);
  const valid =
    number.status === 'valid' &&
    passenger.status !== 'invalid' &&
    trip.status !== 'invalid' &&
    floor.status !== 'invalid' &&
    seatFloorValue.status !== 'invalid' &&
    id.status !== 'invalid' &&
    state.status !== 'invalid';
  return {
    valid,
    number: number.value,
    passengerId: passenger.value,
    passengerStatus: passenger.status,
    tripSlug: trip.value,
    tripStatus: trip.status,
    floor: floor.value,
    floorStatus: floor.status,
    seatFloor: seatFloorValue.value,
    seatFloorStatus: seatFloorValue.status,
    id: id.value,
    idStatus: id.status,
    state: state.value,
    stateStatus: state.status,
  };
};

const hasDuplicateTicketIds = (tickets) => {
  const ids = tickets.filter(({ idStatus }) => idStatus === 'valid').map(({ id }) => id);
  return new Set(ids).size !== ids.length;
};

const permutations = (length) =>
  length === 1
    ? [[0]]
    : [
        [0, 1],
        [1, 0],
      ];

const uniqueBijection = (expected, actual, matcher) => {
  if (expected.length !== actual.length || !PASSENGER_COUNTS.includes(expected.length))
    return false;
  const matches = permutations(expected.length).filter((assignment) => {
    return assignment.every((actualIndex, expectedIndex) => {
      return matcher(actual[actualIndex], expected[expectedIndex]);
    });
  });
  return matches.length === 1;
};

const requestMatches = (actual, expected, mode, segmentTripSlug) => {
  if (!actual.valid || actual.passengerStatus !== 'valid') return false;
  if (segmentTripSlug !== expected.tripSlug || actual.number !== expected.number) return false;
  if (actual.passengerId !== expected.passengerId) return false;
  if (actual.tripStatus === 'valid' && actual.tripSlug !== expected.tripSlug) return false;
  if (mode === 'legacy') return actual.floorStatus === 'missing';

  if (expected.originalRequestFloor === null) return actual.floorStatus === 'missing';
  return actual.floorStatus === 'valid' && actual.floor === expected.originalRequestFloor;
};

const receiptMatches = (actual, expected, mode, segmentTripSlug) => {
  if (!actual.valid || segmentTripSlug !== expected.tripSlug || actual.number !== expected.number) {
    return false;
  }
  if (actual.passengerStatus === 'valid' && actual.passengerId !== expected.passengerId)
    return false;
  if (actual.tripStatus === 'valid' && actual.tripSlug !== expected.tripSlug) return false;
  if (actual.stateStatus === 'valid' && actual.state !== 'locked') return false;

  if (actual.floorStatus === 'valid') {
    if (mode === 'legacy' && expected.numberIdentity !== 'unique') return false;
    return expected.selectedFloor !== null && actual.floor === expected.selectedFloor;
  }
  if (actual.floorStatus !== 'missing') return false;
  if (expected.numberIdentity === 'unique') return true;
  return Boolean(
    mode === 'modern' &&
      expected.requestCanDisambiguateFloor &&
      expected.originalRequestFloor &&
      actual.passengerStatus === 'valid',
  );
};

/**
 * Verifies a finished poll against the exact serialized request and immutable result evidence.
 * @param {object} request - Current request snapshot with ordered expected assignments.
 * @param {object} outcome - Raw observer outcome from one modern or legacy invocation.
 * @returns {boolean} Whether request and receipt form one unique complete bijection.
 */
const completeReceipt = (request, outcome) => {
  if (
    outcome?.successful !== true ||
    outcome?.status !== 'finished' ||
    outcome?.mode !== request.mode ||
    outcome?.token !== request.token ||
    !Array.isArray(outcome.segments) ||
    outcome.segments.length !== 1
  ) {
    return false;
  }

  const [segment] = outcome.segments.map(normalizeSegment);
  if (
    !segment.valid ||
    segment.requestTripSlug !== request.expected[0].tripSlug ||
    (segment.receiptTripStatus === 'valid' &&
      segment.receiptTripSlug !== request.expected[0].tripSlug) ||
    (segment.envelopeTokenStatus === 'valid' && segment.envelopeToken !== request.token) ||
    (segment.containerTokenStatus === 'valid' && segment.containerToken !== request.token) ||
    segment.status !== 'finished' ||
    !segment.requestTickets ||
    !segment.tickets ||
    segment.requestTickets.length !== request.expected.length ||
    segment.tickets.length !== request.expected.length
  ) {
    return false;
  }

  const requestTickets = segment.requestTickets.map(normalizeTicket);
  const receiptTickets = segment.tickets.map(normalizeTicket);
  if (
    requestTickets.some(({ valid }) => !valid) ||
    receiptTickets.some(({ valid }) => !valid) ||
    hasDuplicateTicketIds(receiptTickets)
  ) {
    return false;
  }

  return (
    uniqueBijection(request.expected, requestTickets, (actual, expected) =>
      requestMatches(actual, expected, request.mode, segment.requestTripSlug),
    ) && // eslint-disable-line function-paren-newline
    uniqueBijection(request.expected, receiptTickets, (actual, expected) =>
      receiptMatches(actual, expected, request.mode, segment.requestTripSlug),
    ) // eslint-disable-line function-paren-newline
  );
};

const analyticsPayload = (result) => {
  const matches = result.matches.map(({ match }) => match);
  const overallMatch = matches.every((match) => match === matches[0]) ? matches[0] : 'mixed';
  return {
    'Passenger count': result.passengerCount,
    'Match': overallMatch,
    'Seats': result.matches.map((match, index) => ({
      Way: 'departs',
      Leg: 0,
      Number: normalizeSeatNumber(result.seats[index].number),
      Floor: match.preference.floor,
      Position: match.preference.position ? [...match.preference.position] : null,
      Match: match.match,
    })),
    'Algorithm fallback': result.algorithmFallback,
  };
};

/**
 * Creates the single bounded smart-result owner. Tests inject isolated dependencies; the app uses
 * the singleton export below.
 * @param {object} [dependencies] Runtime dependencies.
 * @param {Function} [dependencies.readPurchase] Current purchase reader.
 * @param {object} [dependencies.initializationOwner] Single-generation initialization authority.
 * @param {Function} [dependencies.track] Smart-seat event reporter.
 * @returns {object} Booking-owned analytics lifecycle.
 */
export const createSmartSeatSelectionResultOwner = ({
  readPurchase = currentPurchase,
  initializationOwner = quickPurchaseInitialization,
  track = trackSmartSeatsSelection,
} = {}) => {
  let current = null;
  let resultGeneration = 0;
  let requestSerial = 0;

  const entryIsCurrent = (entry) => current === entry && entry?.handle?.isCurrent?.() === true;
  const notifyLoadingOwner = (callback, loadingOwnerId) => {
    if (typeof callback !== 'function' || !loadingOwnerId) return;
    try {
      callback(loadingOwnerId);
    } catch (_error) {
      // Loading cleanup is fail-open; reducer owner matching remains the final authority.
    }
  };
  const cancelActiveAttempt = (entry, onCanceled) => {
    const attempt = entry?.activeAttempt;
    if (!attempt?.cancel?.()) return null;
    notifyLoadingOwner(onCanceled, attempt.loadingOwnerId);
    return attempt.loadingOwnerId;
  };

  /**
   * Ensures one bounded booking entry for the current initialization handle.
   * Replacing the handle cancels the prior request capability without retaining attempt history.
   * @param {object} params - Booking identity.
   * @param {object} params.handle - Current initialization capability.
   * @param {string} params.token - Exact purchase token.
   * @param {string} params.itineraryKey - Immutable itinerary identity.
   * @returns {object|null} Current entry when the handle owns the booking.
   */
  const ensureBooking = ({ handle, token, itineraryKey }) => {
    if (!handle?.isCurrent?.() || handle.token !== token || handle.itineraryKey !== itineraryKey) {
      return null;
    }
    if (current && current.handle === handle) return current;
    cancelActiveAttempt(current);
    current = {
      token,
      itineraryKey,
      handle,
      initialization: null,
      result: null,
      analyticsRequest: null,
      activeAttempt: null,
    };
    return current;
  };

  /**
   * Starts or reuses the exact initialization promise for a current booking and remount.
   * @param {object} params - Initialization identity and operation.
   * @param {string} params.token - Exact purchase token.
   * @param {string} params.itineraryKey - Immutable itinerary identity.
   * @param {Function} params.operation - One orchestration operation.
   * @returns {{handle: object, promise: Promise, reused: boolean}|null} Shared initialization.
   */
  const beginOrReuseInitialization = ({ token, itineraryKey, operation }) => {
    if (typeof operation !== 'function') return null;
    if (
      current?.token === token &&
      current.itineraryKey === itineraryKey &&
      current.initialization &&
      entryIsCurrent(current)
    ) {
      return {
        handle: current.handle,
        promise: current.initialization.promise,
        reused: true,
      };
    }

    let entry = current;
    if (
      !entry ||
      entry.token !== token ||
      entry.itineraryKey !== itineraryKey ||
      !entryIsCurrent(entry)
    ) {
      const purchase = readPurchase() || {};
      const handle = initializationOwner.begin({ token, itineraryKey, purchase });
      entry = ensureBooking({ handle, token, itineraryKey });
    }
    if (!entry) return null;

    const initialization = { state: 'pending', outcome: null, promise: null };
    const promise = Promise.resolve().then(() => operation(entry.handle));
    initialization.promise = promise;
    entry.initialization = initialization;
    promise.then(
      (outcome) => {
        if (current === entry) {
          initialization.state = 'settled';
          initialization.outcome = outcome;
        }
      },
      () => {
        if (current === entry) initialization.state = 'rejected';
      },
    );
    return { handle: entry.handle, promise, reused: false };
  };

  /**
   * Returns retained initialization only while its handle owns the same booking.
   * @param {object} params - Token and immutable itinerary key.
   * @param {string} params.token - Exact purchase token.
   * @param {string} params.itineraryKey - Immutable itinerary identity.
   * @returns {{handle: object, promise: Promise, reused: boolean}|null} Retained initialization.
   */
  const getInitialization = ({ token, itineraryKey } = {}) => {
    if (
      current?.token !== token ||
      current.itineraryKey !== itineraryKey ||
      !current.initialization ||
      !entryIsCurrent(current)
    ) {
      return null;
    }
    return { handle: current.handle, promise: current.initialization.promise, reused: true };
  };

  /**
   * Retains one immutable smart result and cancels any prior result-scoped lock attempt.
   * @param {object} params - Current selection inputs and ownership handle.
   * @param {object} params.handle - Current initialization capability.
   * @param {string} params.token - Exact purchase token.
   * @param {string} params.itineraryKey - Immutable itinerary identity.
   * @param {object} params.trips - Ordered lock trip snapshot.
   * @param {object} params.smartSelection - Raw smart-selection result.
   * @param {Function} [params.onAttemptCanceled] Immediate owner-matched loading release callback.
   * @returns {object|null} Result-generation context for nested async guards.
   */
  const retainSelection = ({
    handle,
    token,
    itineraryKey,
    trips,
    smartSelection,
    onAttemptCanceled,
  }) => {
    const entry = ensureBooking({ handle, token, itineraryKey });
    const result = normalizeSmartResult(smartSelection);
    if (
      !entry ||
      !result ||
      !entryIsCurrent(entry) ||
      !currentTripMatches(readPurchase() || {}, result) ||
      !tripMatchesResult(trips, result)
    ) {
      return null;
    }

    const fingerprint = selectionFingerprint(result);
    if (entry.result?.fingerprint === fingerprint && entry.result.state !== 'canceled') {
      return entry.result.context;
    }

    cancelActiveAttempt(entry, onAttemptCanceled);
    resultGeneration += 1;
    const resultState = {
      generation: resultGeneration,
      fingerprint,
      value: result,
      state: 'pending',
      emissionAttempted: false,
      context: null,
    };
    const entryForResult = entry;
    resultState.context = Object.freeze({
      generation: resultGeneration,
      token,
      itineraryKey,
      fingerprint,
      isCurrent: () =>
        entryIsCurrent(entryForResult) &&
        entryForResult.result?.generation === resultState.generation &&
        entryForResult.result?.state !== 'canceled',
    });
    entry.result = resultState;
    entry.analyticsRequest = null;
    return resultState.context;
  };

  /**
   * Marks only the exact retained result failed and cancels its active booking attempt.
   * @param {object} params - Initialization, token and result-generation identity.
   * @param {object} params.handle - Current initialization capability.
   * @param {string} params.token - Exact purchase token.
   * @param {object} params.resultContext - Exact retained result capability.
   * @returns {boolean} Whether the exact current result was marked failed.
   */
  const markSelectionFailed = ({ handle, token, resultContext } = {}) => {
    if (!entryIsCurrent(current) || current.token !== token || current.handle !== handle)
      return false;
    if (
      !current.result ||
      current.result.context !== resultContext ||
      current.result.state === 'consumed'
    ) {
      return false;
    }
    current.activeAttempt?.cancel?.();
    current.result.state = 'failed';
    current.analyticsRequest = null;
    return true;
  };

  /**
   * Recovers the unchanged pending/failed result for an explicit passenger correction.
   * @param {object} params - Current token and selected trip snapshot.
   * @param {string} params.token - Exact purchase token.
   * @param {object} params.trips - Current ordered trip/seat snapshot.
   * @returns {{handle: object, context: object}|null} Existing ownership, never a new result.
   */
  const retrySelectionForUnchangedTrip = ({ token, trips } = {}) => {
    if (
      !entryIsCurrent(current) ||
      current.token !== token ||
      !current.result ||
      !['pending', 'failed'].includes(current.result.state) ||
      !currentTripMatches(readPurchase() || {}, current.result.value) ||
      !tripMatchesResult(trips, current.result.value)
    ) {
      return null;
    }
    return { handle: current.handle, context: current.result.context };
  };

  /**
   * Creates one immutable request and a distinct loading capability under the current handle.
   * Analytics consumption clears `analyticsRequest` but leaves `activeAttempt` current until its
   * lock action finishes ticket publication and refresh. A later request cancels the attempt first.
   * @param {object} params - Current result, passenger and transport inputs.
   * @param {object} params.handle - Current initialization capability.
   * @param {string} params.token - Exact purchase token.
   * @param {object} params.trips - Exact ordered lock trip snapshot.
   * @param {Array<object>} params.passengers - Persisted passengers with stable identifiers.
   * @param {object} params.resultContext - Exact retained result capability.
   * @param {'modern'|'legacy'} [params.mode='modern'] - Lock transport shape.
   * @param {Function} [params.onAttemptCanceled] Immediate owner-matched loading release callback.
   * @returns {object|null} Observer plus bounded attempt lifecycle.
   */
  const beginReservation = ({
    handle,
    token,
    trips,
    passengers,
    resultContext,
    mode = 'modern',
    onAttemptCanceled,
  } = {}) => {
    if (
      !entryIsCurrent(current) ||
      current.handle !== handle ||
      current.token !== token ||
      !current.result ||
      current.result.context !== resultContext ||
      !['pending', 'failed'].includes(current.result.state) ||
      !['modern', 'legacy'].includes(mode) ||
      !currentTripMatches(readPurchase() || {}, current.result.value) ||
      !tripMatchesResult(trips, current.result.value)
    ) {
      return null;
    }

    const passengerIds = uniquePassengerIds(passengers, current.result.value.passengerCount);
    if (!passengerIds) return null;

    cancelActiveAttempt(current, onAttemptCanceled);
    requestSerial += 1;
    const request = {
      id: requestSerial,
      token,
      mode,
      resultGeneration: current.result.generation,
      expected: immutableClone(expectedTicketsFor(current.result.value, passengerIds, mode)),
    };
    current.analyticsRequest = request;
    current.result.state = 'pending';
    const entry = current;

    let attemptState = 'current';
    let attemptOwnership;
    const cancelAttempt = () => {
      if (attemptState !== 'current') return false;
      attemptState = 'canceled';
      if (entry.activeAttempt === attemptOwnership) entry.activeAttempt = null;
      if (entry.analyticsRequest === request) entry.analyticsRequest = null;
      return true;
    };
    const settleAttempt = () => {
      if (attemptState !== 'current') return false;
      attemptState = 'settled';
      if (entry.activeAttempt === attemptOwnership) entry.activeAttempt = null;
      if (entry.analyticsRequest === request) entry.analyticsRequest = null;
      return true;
    };
    attemptOwnership = Object.freeze({
      loadingOwnerId: createQuickPurchaseAttemptLoadingOwnerId(
        handle,
        `result-${request.resultGeneration}-request-${request.id}`,
      ),
      isCurrent: () =>
        attemptState === 'current' &&
        entryIsCurrent(entry) &&
        entry.activeAttempt === attemptOwnership &&
        entry.result?.generation === request.resultGeneration &&
        entry.result?.state !== 'canceled',
      canReleaseLoading: () => attemptState === 'canceled',
      getStatus: () => attemptState,
      cancel: cancelAttempt,
      settle: settleAttempt,
    });
    entry.activeAttempt = attemptOwnership;

    return Object.freeze({
      requestId: request.id,
      attemptOwnership,
      cancel: cancelAttempt,
      settle: settleAttempt,
      observe: (outcome) => {
        if (
          !entryIsCurrent(entry) ||
          entry.analyticsRequest !== request ||
          entry.activeAttempt !== attemptOwnership ||
          !attemptOwnership.isCurrent() ||
          entry.result?.generation !== request.resultGeneration ||
          !currentTripMatches(readPurchase() || {}, entry.result.value)
        ) {
          return { accepted: false, stale: true };
        }

        if (!completeReceipt(request, outcome)) {
          entry.result.state = 'failed';
          entry.analyticsRequest = null;
          return { accepted: false, stale: false };
        }

        entry.result.emissionAttempted = true;
        entry.result.state = 'consumed';
        entry.analyticsRequest = null;
        if (
          entry.initialization &&
          entry.initialization.state !== 'pending' &&
          entry.initialization.outcome?.success !== true
        ) {
          const recoveredOutcome = immutableClone({
            success: true,
            smartSelection: entry.result.value,
          });
          entry.initialization.state = 'settled';
          entry.initialization.outcome = recoveredOutcome;
          entry.initialization.promise = Promise.resolve(recoveredOutcome);
        }
        const payload = analyticsPayload(entry.result.value);
        try {
          const attempt = track(payload);
          if (attempt && typeof attempt.then === 'function') {
            Promise.resolve(attempt).catch(() => undefined);
          }
        } catch (_error) {
          // Analytics is intentionally fail-open after the synchronous one-attempt claim.
        }
        return { accepted: true, stale: false };
      },
    });
  };

  /**
   * Cancels the exact current active attempt without granting authority to an older handle.
   * @param {object} params - Optional matching handle and token.
   * @param {object} [params.handle] Exact initialization capability when known.
   * @param {string} [params.token] Exact purchase token when known.
   * @returns {string|null} Owner-matched loading id to release immediately.
   */
  const invalidateLoadingAttempt = ({ handle, token } = {}) => {
    if (!current || (handle && current.handle !== handle) || (token && current.token !== token)) {
      return null;
    }
    return cancelActiveAttempt(current);
  };

  /**
   * Invalidates pending smart association without disposing the shared booking generation.
   * @param {object} params - Optional matching token.
   * @param {string} [params.token] Exact purchase token when known.
   * @returns {boolean} Whether a current booking was invalidated.
   */
  const invalidateSelection = ({ token } = {}) => {
    if (!current || (token && current.token !== token)) return false;
    current.activeAttempt?.cancel?.();
    if (current.result) current.result.state = 'canceled';
    current.result = null;
    current.analyticsRequest = null;
    return true;
  };

  /**
   * Disposes one matching booking and permanently invalidates its initialization handle.
   * @param {object} params - Optional matching token.
   * @param {string} [params.token] Exact purchase token when known.
   * @param {Function} [params.onLoadingOwnerDisposed] Synchronous owner-matched release callback.
   * @returns {boolean} Whether retained booking state was released.
   */
  const disposeBooking = ({ token, onLoadingOwnerDisposed } = {}) => {
    const matchesCurrent = Boolean(current && (!token || current.token === token));
    if (matchesCurrent) {
      const { handle } = current;
      const handleStatus =
        handle?.getStatus?.() || (handle?.isCurrent?.() ? 'current' : 'replaced');
      const mayReleaseRetainedLoading = ['current', 'invalidated'].includes(handleStatus);
      cancelActiveAttempt(current, mayReleaseRetainedLoading ? onLoadingOwnerDisposed : null);
      if (mayReleaseRetainedLoading) {
        notifyLoadingOwner(onLoadingOwnerDisposed, handle.loadingOwnerId);
      }
      current = null;
    }
    initializationOwner.invalidate({ token });
    return matchesCurrent;
  };

  const getSnapshot = () => {
    if (!current) return null;
    return {
      token: current.token,
      itineraryKey: current.itineraryKey,
      initializationState: current.initialization?.state || null,
      result: current.result
        ? {
            generation: current.result.generation,
            state: current.result.state,
            emissionAttempted: current.result.emissionAttempted,
            seatCount: current.result.value.seats.length,
          }
        : null,
      analyticsRequest: current.analyticsRequest
        ? { id: current.analyticsRequest.id, mode: current.analyticsRequest.mode }
        : null,
      activeAttempt: current.activeAttempt
        ? {
            loadingOwnerId: current.activeAttempt.loadingOwnerId,
            status: current.activeAttempt.getStatus(),
          }
        : null,
    };
  };

  return {
    beginOrReuseInitialization,
    getInitialization,
    ensureBooking,
    retainSelection,
    markSelectionFailed,
    retrySelectionForUnchangedTrip,
    beginReservation,
    invalidateLoadingAttempt,
    invalidateSelection,
    disposeBooking,
    getSnapshot,
  };
};

const smartSeatSelectionResult = createSmartSeatSelectionResultOwner();

export default smartSeatSelectionResult;
